import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import api from "@/lib/api";

/**
 * Iter88zbs (2026-02-19) — AutofillInput.
 *
 * Reusable Text-Input mit:
 *  - Nativem Browser-Autofill via `autoComplete`-HTML-Attribut
 *  - Client-seitigem LocalStorage-MRU (sofort verfügbar, offline)
 *  - Backend-Historie via `/api/autofill/{storageKey}` (geräteübergreifend)
 *  - Gmail-artigem Dropdown beim Fokus/Tippen
 *  - Commit-Callback speichert Wert in beiden Speichern
 *
 * Props:
 *   value / onChange       — kontrollierter Input
 *   storageKey             — z.B. "cc_email" (nur a-z, 0-9, _-, max 64)
 *   type / autoComplete    — an das native Input weitergereicht
 *   splitOn                — z.B. `[";", ","]` bei CC-Listen; Vorschläge
 *                            werden dann auf das aktuelle Token (nach
 *                            letztem Separator) angewendet.
 *   commitOnBlur           — default true; speichert beim Verlassen
 *   dataTestid             — Test-ID (Input trägt sie, Dropdown-Optionen
 *                            bekommen `${dataTestid}-suggest-{value}`)
 *   className, placeholder — an Input weitergereicht
 */
const LS_PREFIX = "novatus:autofill:v1:";
const MAX_LOCAL = 20;

const loadLocal = (key) => {
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === "string" && x.trim()) : [];
  } catch { return []; }
};

const saveLocal = (key, value) => {
  try {
    const v = (value || "").trim();
    if (!v) return;
    const existing = loadLocal(key).filter((x) => x !== v);
    const next = [v, ...existing].slice(0, MAX_LOCAL);
    localStorage.setItem(LS_PREFIX + key, JSON.stringify(next));
  } catch { /* quota / private mode — silent */ }
};

const removeLocal = (key, value) => {
  try {
    const v = (value || "").trim();
    if (!v) return;
    const next = loadLocal(key).filter((x) => x !== v);
    localStorage.setItem(LS_PREFIX + key, JSON.stringify(next));
  } catch { /* ignore */ }
};

export default function AutofillInput({
  value,
  onChange,
  storageKey,
  type = "text",
  autoComplete,
  splitOn,
  commitOnBlur = true,
  dataTestid,
  className,
  placeholder,
  ...rest
}) {
  const [suggestions, setSuggestions] = useState([]);   // aus Backend
  const [localMru, setLocalMru] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  // Iter88zbs-fix: dirty-Flag verhindert, dass vorbelegte Werte
  // (z.B. aus Kunden-DB) beim ersten Blur in die eigene Historie
  // rutschen — es wird nur committet, wenn der User den Wert wirklich
  // getippt/geändert hat.
  const dirtyRef = useRef(false);

  // 1) LocalStorage bei Mount + StorageKey-Wechsel laden
  useEffect(() => {
    setLocalMru(loadLocal(storageKey));
  }, [storageKey]);

  // 2) Backend fetchen (debounced) — beim Fokus initial + bei Änderung
  const fetchBackend = useCallback(async (q) => {
    if (!storageKey) return;
    setLoading(true);
    try {
      const params = { limit: 15 };
      if (q && q.trim()) params.q = q.trim();
      const r = await api.get(`/autofill/${storageKey}`, { params });
      setSuggestions(Array.isArray(r.data) ? r.data.map((x) => x.value) : []);
    } catch { setSuggestions([]); }
    finally { setLoading(false); }
  }, [storageKey]);

  // Aktuelles Token — bei Multi-Value-Feldern nur den Teil nach letztem Separator
  const currentToken = useMemo(() => {
    if (!splitOn || splitOn.length === 0) return value || "";
    const sepRe = new RegExp(`[${splitOn.map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("")}]`);
    const parts = (value || "").split(sepRe);
    return (parts[parts.length - 1] || "").trim();
  }, [value, splitOn]);

  // Debounced fetch beim Tippen
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchBackend(currentToken), 180);
    return () => clearTimeout(t);
  }, [currentToken, open, fetchBackend]);

  // Merge Local + Backend, dedupliziert, filtert nach currentToken
  const merged = useMemo(() => {
    const seen = new Set();
    const out = [];
    const needle = currentToken.toLowerCase();
    // LocalStorage-MRU zuerst → "gefühlt sofort"
    for (const v of localMru) {
      if (!v) continue;
      if (needle && !v.toLowerCase().includes(needle)) continue;
      if (seen.has(v)) continue;
      seen.add(v); out.push({ value: v, source: "local" });
      if (out.length >= 8) break;
    }
    for (const v of suggestions) {
      if (!v || seen.has(v)) continue;
      if (needle && !v.toLowerCase().includes(needle)) continue;
      seen.add(v); out.push({ value: v, source: "backend" });
      if (out.length >= 15) break;
    }
    // Wenn der aktuelle Wert exakt einer der Vorschläge ist, nicht anzeigen.
    return out.filter((s) => s.value !== (value || "") && s.value !== currentToken);
  }, [localMru, suggestions, currentToken, value]);

  // Klick außerhalb → schließen
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const commit = useCallback(async (v) => {
    const raw = (v ?? value ?? "").trim();
    if (!raw || !storageKey) return;
    if (!dirtyRef.current) return; // Nichts speichern wenn User nichts geändert hat
    // Commit-Hygiene:
    //  - Trailing Separatoren (";", ",") entfernen
    //  - Bei splitOn: nur die einzelnen Tokens committen (nicht den Kombi-
    //    String — der ist bei Multi-Value nie ein sinnvoller Vorschlag)
    //  - Bei E-Mail-Keys (Endung „_email"): nur Werte mit „@" akzeptieren,
    //    damit unfertige Fragmente wie „cc.qa" nicht in die Historie
    //    wandern.
    const isEmailKey = /email$/.test(storageKey);
    const looksLikeEmail = (s) => /@[^\s@;,]+\./.test(s || "");
    const acceptable = (s) => (isEmailKey ? looksLikeEmail(s) : (s || "").trim().length > 0);

    let candidates;
    if (splitOn && splitOn.length) {
      const sepRe = new RegExp(`[${splitOn.map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("")}]`);
      candidates = raw.split(sepRe).map((t) => t.trim()).filter(Boolean);
    } else {
      candidates = [raw.replace(/[;,\s]+$/, "").trim()];
    }
    const valid = Array.from(new Set(candidates.filter(acceptable)));
    if (valid.length === 0) { dirtyRef.current = false; return; }

    for (const v2 of valid) {
      saveLocal(storageKey, v2);
    }
    setLocalMru(loadLocal(storageKey));
    try {
      // Backend speichert einzeln (das Backend würde bei einem Kombi-String
      // sonst zusätzlich noch mal splitten und den Kombi-String selbst
      // ablegen — nicht gewünscht).
      for (const v2 of valid) {
        await api.post(`/autofill/${storageKey}`, { value: v2 });
      }
    } catch { /* offline / auth-timeout — LocalStorage bleibt */ }
    dirtyRef.current = false;
  }, [value, storageKey, splitOn]);

  const applySuggestion = (sug) => {
    if (!splitOn || splitOn.length === 0) {
      onChange(sug);
    } else {
      // Ersetzt nur das aktuelle Token und hängt "; " als Separator an,
      // damit der User direkt die nächste Adresse tippen kann.
      const sepRe = new RegExp(`[${splitOn.map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("")}]`);
      const parts = (value || "").split(sepRe);
      parts[parts.length - 1] = sug;
      onChange(parts.join("; ") + "; ");
    }
    // Suggestion-Übernahme zählt als User-Aktion → committen erlauben.
    dirtyRef.current = true;
    setOpen(false);
    setHighlight(-1);
    // Fokus zurück, damit User weiterschreiben kann
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const removeSuggestion = async (e, sug) => {
    e.preventDefault();
    e.stopPropagation();
    removeLocal(storageKey, sug);
    setLocalMru(loadLocal(storageKey));
    try {
      await api.delete(`/autofill/${storageKey}`, { params: { value: sug } });
    } catch { /* ignore */ }
    setSuggestions((prev) => prev.filter((v) => v !== sug));
  };

  const onKeyDown = (e) => {
    if (!open || merged.length === 0) {
      if (e.key === "Enter" && commitOnBlur) {
        commit();
      }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, merged.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, -1)); }
    else if (e.key === "Enter" || e.key === "Tab") {
      if (highlight >= 0 && highlight < merged.length) {
        e.preventDefault();
        applySuggestion(merged[highlight].value);
      } else if (e.key === "Enter" && commitOnBlur) {
        commit();
      }
    } else if (e.key === "Escape") {
      setOpen(false); setHighlight(-1);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        ref={inputRef}
        type={type}
        autoComplete={autoComplete}
        value={value || ""}
        onChange={(e) => { dirtyRef.current = true; onChange(e.target.value); if (!open) setOpen(true); setHighlight(-1); }}
        onFocus={() => { setOpen(true); fetchBackend(currentToken); }}
        onBlur={() => { if (commitOnBlur) commit(); }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        data-testid={dataTestid}
        className={className}
        {...rest}
      />
      {open && merged.length > 0 && (
        <div
          data-testid={dataTestid ? `${dataTestid}-suggest-list` : undefined}
          className="absolute left-0 right-0 top-full mt-1 z-50 bg-[#0E0E12] border border-zinc-700 rounded-xl shadow-2xl max-h-64 overflow-y-auto"
        >
          {loading && (
            <div className="px-3 py-2 text-[12px] text-zinc-500">Lade …</div>
          )}
          {merged.map((s, i) => {
            const isActive = i === highlight;
            return (
              <div
                key={s.value}
                role="option"
                aria-selected={isActive}
                data-testid={dataTestid ? `${dataTestid}-suggest-${i}` : undefined}
                onMouseDown={(e) => { e.preventDefault(); applySuggestion(s.value); }}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-[14px] transition cursor-pointer ${
                  isActive ? "bg-[#00E575]/12 text-[#00E575]" : "text-zinc-100 hover:bg-zinc-800/50"
                }`}
              >
                <span className="flex-1 truncate">{s.value}</span>
                <span className={`text-[10px] font-mono uppercase tracking-wider ${
                  s.source === "local" ? "text-zinc-500" : "text-zinc-400"
                }`}>
                  {s.source === "local" ? "kürzlich" : "verlauf"}
                </span>
                <button
                  type="button"
                  onMouseDown={(e) => removeSuggestion(e, s.value)}
                  title="Vorschlag entfernen"
                  className="p-1 rounded hover:bg-rose-500/20 text-zinc-500 hover:text-rose-300 transition"
                  tabIndex={-1}
                >
                  <X size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
